/**
 * Module dependencies.
 */

import { Text, baseFontSize, clampCalc } from 'src/components/core/text';
import { media } from 'src/styles/media';
import { normalizeNewLine } from 'src/core/utils/string';
import Container from 'src/components/core/layout/container';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import RawHtml from 'src/components/core/raw-html';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Section` styled component.
 */

const Section = styled.section`
  padding-bottom: 2rem;

  ${media.min.md`
    padding-bottom: 10.25rem;
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  ${media.min.md`
    display: grid;
    grid-template-areas: '. content .';
    grid-template-columns: 3fr 6fr 3fr;
  `}
`;

/**
 * `Content` styled component.
 */

const Content = styled.div`
  ${media.min.md`
    grid-area: content;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Text)`
  letter-spacing: clamp(
    ${6.75 / baseFontSize}rem,
    ${clampCalc(6.75, 14.4)},
    ${14.4 / baseFontSize}rem
  );
  margin-bottom: 1.25rem;
  text-align: center;
  text-transform: uppercase;

  ${media.min.md`
    margin-bottom: 2.5rem;
  `}
`;

/**
 * `Subtitle` styled component.
 */

const Subtitle = styled(Text)`
  margin-bottom: 2rem;
  text-align: center;
  text-transform: uppercase;

  ${media.min.md`
    margin-bottom: 5rem;
  `}
`;

/**
 * `Description` styled component.
 */

const Description = styled(RawHtml)`
  font-family: var(--font-futura-pt);
  font-size: clamp(
    ${16 / baseFontSize}rem,
    ${clampCalc(16, 19)},
    ${19 / baseFontSize}rem
  );
  letter-spacing: clamp(
    ${0.4 / baseFontSize}rem,
    ${clampCalc(0.4, 0.47)},
    ${0.47 / baseFontSize}rem
  );
  line-height: clamp(
    ${20 / baseFontSize}rem,
    ${clampCalc(20, 30)},
    ${30 / baseFontSize}rem
  );
`;

/**
 * Export `TripHeaderSection` component.
 */

const TripHeaderSection = () => {
  const { translate } = useTranslate();

  return (
    <Section>
      <Container>
        <Grid>
          <Content>
            <FadeInUpAnimation
              options={{
                distance: '100%',
                transitionTime: 1
              }}
            >
              <Title
                as={'h2'}
                variant={'header'}
              >
                {translate('trip-to-portugal:contentSection.title')}
              </Title>
            </FadeInUpAnimation>

            <FadeInUpAnimation
              options={{
                distance: '100%',
                transitionTime: 1
              }}
            >
              <Subtitle
                as={'p'}
                variant={'titleXS'}
              >
                <RawHtml>
                  {normalizeNewLine(
                    translate('trip-to-portugal:contentSection.subtitle')
                  )}
                </RawHtml>
              </Subtitle>
            </FadeInUpAnimation>

            <FadeInUpAnimation
              options={{
                distance: '25%',
                transitionTime: 1
              }}
            >
              <Description>
                {normalizeNewLine(
                  translate('trip-to-portugal:contentSection.description')
                )}
              </Description>
            </FadeInUpAnimation>
          </Content>
        </Grid>
      </Container>
    </Section>
  );
};

/**
 * Export `TripHeaderSection` component.
 */

export default TripHeaderSection;
